'use client';

import { Event } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  type SxProps,
  styled,
} from '@mui/material';
import { type FocusEvent, type ReactNode, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { format } from '@campaign-manager/core/lib/date';
import { MfDate } from '@campaign-manager/core/model';
import type { TextFieldProps } from '@campaign-manager/react/components';

import usePopover from '../../hooks/usePopover';
import TextField from '../TextField';

const DatePickerWrapper = styled(Box)(
  ({ theme }) => `
& {
  .react-datepicker {
    border: none;
  }

  .react-datepicker__header {
    background: none;
    border-radius: unset;
    border-color: #e5e5e5;
  }

  .react-datepicker__navigation{
    width: 48px;
    height: 48px;
  }

  .react-datepicker__current-month{
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .react-datepicker__day{
    font-size: 12px;
  }

  .react-datepicker__day--selected, .react-datepicker__day--in-range {
    background-color: ${theme.palette.secondary.main};
    font-size: 12px;
    font-weight: 500;
  }
},
`,
);

export type DatePickerValue = MfDate | undefined;

interface Props {
  locale?: string;

  name?: string;
  value?: DatePickerValue;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  label?: string;

  onChange?: (value: DatePickerValue) => void;
  onBlur?: (e: FocusEvent<any>) => void;

  minDate?: MfDate;
  maxDate?: MfDate;

  disabled?: boolean;
  slotProps?: TextFieldProps['slotProps'];

  variant?: 'standard' | 'outlined' | 'filled';
  sx?: SxProps;
  popperSx?: SxProps;
  elevation?: number;

  showOnFieldClick?: boolean;
}

// #NOTE: Since we use it in a Next.js app, the react-datepicker styles can only be
// imported from the app's root layout (see apps/campaign-manager/app/layout.tsx)
export default function DatePicker({
  locale = 'en',
  name,
  value: initValue,
  required,
  error,
  helperText,
  label,
  onChange: onChangeCallback,
  onBlur,
  minDate,
  maxDate,
  disabled,
  slotProps,
  variant = 'filled',
  sx,
  popperSx,
  showOnFieldClick,
}: Props) {
  const { anchorEl, open, id, handleClick, handleKeyboardEvent, handleClose } = usePopover();

  const [selected, setSelected] = useState<MfDate | undefined>();

  useEffect(() => {
    setSelected(initValue);
  }, [initValue]);

  const onChange = (value: Date | null) => {
    if (value) {
      const newValue = MfDate.fromDateParts(
        value.getFullYear(),
        value.getMonth() + 1,
        value.getDate(),
        0,
      );
      setSelected(newValue);
      onChangeCallback?.(newValue);
    } else {
      setSelected(undefined);
      onChangeCallback?.(undefined);
    }

    handleClose();
  };

  const pickerSelectedDate = selected
    ? new Date(selected.year, selected.month - 1, selected.day)
    : undefined;
  const pickerMinDate = minDate
    ? new Date(minDate.year, minDate.month - 1, minDate.day)
    : undefined;
  const pickerMaxDate = maxDate
    ? new Date(maxDate.year, maxDate.month - 1, maxDate.day)
    : undefined;

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <TextField
        variant={variant}
        slotProps={{
          ...slotProps,
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={handleClick}
                  disabled={disabled}
                  edge="start"
                  aria-label="Open calendar"
                >
                  <Event fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            ...slotProps?.input,
          },
        }}
        name={name}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            return;
          }
          handleKeyboardEvent(e);
        }}
        {...(showOnFieldClick ? { onClick: handleClick } : {})}
        value={selected ? format(selected) : ''}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        label={label}
        sx={sx}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={handleClose}
      >
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1201, ...popperSx }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <Paper
            sx={{
              height: 308,
              borderRadius: 'var(--paperBorderRadius-default)',
            }}
            elevation={3}
          >
            <Stack
              direction="row"
              sx={{ height: '100%', overflow: 'hidden' }}
            >
              <Stack>
                <DatePickerWrapper>
                  <ReactDatePicker
                    locale={locale}
                    onChange={onChange}
                    selected={pickerSelectedDate}
                    inline
                    monthsShown={1}
                    {...(minDate && { minDate: pickerMinDate })}
                    {...(maxDate && { maxDate: pickerMaxDate })}
                    disabled={disabled}
                  />
                </DatePickerWrapper>
              </Stack>
            </Stack>
          </Paper>
        </Popper>
      </ClickAwayListener>
    </Box>
  );
}
